import styled, { css } from 'styled-components';
import colors from 'styles/colors';
import { SettingsSubtitle } from 'styles/settings';

export const INPUT_HIGHLIGHT_SHADOW = `0 2px 4px 0 ${colors.highlightShadow}`;
export const INPUT_BORDER_RADIUS = '4px';
export const INPUT_BORDER_WIDTH = 1;
export const INPUT_BORDER = `${INPUT_BORDER_WIDTH}px solid ${colors.fleetOffGray}`;
export const INPUT_PADDING = '7.5px 8px';
export const FORM_GUTTER_PX = 30;

export const FormGroup = styled.div`
  margin-bottom: 10px;
`;

export const Label = styled.label`
  display: block;
  font-size: 11px;
  line-height: 15px;
  margin-bottom: 12px;
  font-weight: normal;
  opacity: 0.5;
  text-transform: uppercase;
`;

export const INPUT_FOCUS_MIXIN = css`
  border: 1px solid ${colors.highlight};
  box-shadow: ${INPUT_HIGHLIGHT_SHADOW};
`;

export const INPUT_STYLES_MIXIN = css`
  background: ${colors.gray90};
  line-height: 1em;
  padding: ${INPUT_PADDING};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primary};
  border-radius: ${INPUT_BORDER_RADIUS};
  border: ${INPUT_BORDER};
  transition: 0.15s border, 0.15s box-shadow ease-in-out;

  &:focus {
    ${INPUT_FOCUS_MIXIN};
  }

  &:hover:not(:focus) {
    border-color: ${colors.gray70};
  }

  &:disabled:not(:read-only) {
    background: ${colors.gray90};
    border: ${INPUT_BORDER};
    box-shadow: none;
    cursor: not-allowed;
    color: ${colors.disabledText};
    opacity: 1;
  }

  &::-webkit-input-placeholder {
    color: ${colors.inactive};
  }
`;

export const READ_ONLY_MIXIN = css`
  &:read-only,
  &:disabled:read-only {
    background: none;
    border: none;
    border-color: #0000;
    box-shadow: none;
    color: ${colors.primary};
    padding: 0;
    opacity: 1;
  }
`;

export const Input = styled.input`
  ${INPUT_STYLES_MIXIN};
  ${READ_ONLY_MIXIN};
  padding: 5px 8px;
`;

export const InputContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  input {
    flex-grow: 1;
  }

  > * {
    margin-left: 0.5em;
  }

  *:first-child {
    margin-left: 0;
  }
`;

export const InputTextIndicator = styled.span`
  font-size: 1.3em;
`;
export const InputSymbolIndicator = styled.span`
  font-size: 1.5em;
`;

export const Checkbox = styled.div<{ checked?: boolean; disabled?: boolean }>`
  align-items: center;
  border: 2px solid ${colors.gray55};
  border-radius: 3px;
  color: ${colors.white};
  background-color: ${colors.white};
  cursor: pointer;
  display: flex;
  height: 1.285em;
  width: 1.285em;
  position: relative;
  justify-content: center;
  transition: 0.15s background-color, 0.15s border linear;

  i {
    display: none;
    font-size: 0.65em;
  }

  ${(props) =>
    props.checked &&
    `
            background-color: ${colors.highlight};
            border: 2px solid ${colors.highlight};

            i {
                display: block;
            }
        `};
  ${(props) =>
    props.disabled &&
    `
            background-color: ${colors.inactive};
            cursor: auto;
        `};

  ${(props) =>
    props.checked &&
    props.disabled &&
    `
            background-color: ${colors.inactive};
            border: 2px solid ${colors.inactive};
        `};
`;

export const Textarea = styled.textarea`
  ${INPUT_STYLES_MIXIN};
  ${READ_ONLY_MIXIN};
  width: 100%;
  font-size: 13px;
  resize: none;

  &[disabled] {
    color: ${colors.disabledText};
    border-color: ${colors.disabledBorder};
  }

  &::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: ${colors.inactive};
  }

  &::-moz-placeholder {
    /* Firefox 19+ */
    color: ${colors.inactive};
  }

  &:-ms-input-placeholder {
    /* IE 10+ */
    color: ${colors.inactive};
  }

  &:-moz-placeholder {
    /* Firefox 18- */
    color: ${colors.inactive};
  }
`;

export const BorderedTextarea = styled(Textarea)`
  padding: 7.5px 15px;
  border: 1px solid ${colors.gray60};
  box-shadow: 0 2px 4px 0 ${colors.gray85};
  transition: 0.15s border, 0.15s box-shadow ease-in-out;
`;

export const InputHintText = styled.span`
  display: inline-block;
  font-size: 10px;
  line-height: 14px;
  margin: 0;
  padding-left: 2px;
`;

export const ErrorMessageText = styled(InputHintText)`
  color: ${colors.danger};
`;

export const GlobalErrors = styled.div`
  font-weight: 600;
  > span {
    font-size: 12px;
    line-height: 17px;
  }
`;

export const RadioWrapper = styled.div``;

export const FormTitle = styled(SettingsSubtitle)`
  margin-top: 40px;
`;

export const FormDivider = styled(SettingsSubtitle)`
  margin-top: 1em;
`;

export const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
`;

export const FormWrapper = styled.div`
  & + & {
    margin-top: 40px;
  }
`;

export const DropdownContainer = styled.div`
  > div {
    width: 100%;
    position: unset;

    button {
      width: 100%;
      height: 30px;
      font-size: 12px;
      line-height: 1.2em;
    }

    div {
      width: calc(100% - 1em);
      text-align: left;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`;

export const FormItem = styled.div`
  float: left;
  margin-bottom: 1em;
  width: calc(50% - (${FORM_GUTTER_PX}px / 2));

  ${DropdownContainer} {
    width: 100%;
    button {
      margin-top: 0;
    }
  }
`;

export const UntitledFormItem = styled.div`
  margin-top: 36px;
`;

export const FullWidthFormItem = styled(FormItem)`
  width: 100%;
`;

export const LargeSelector = styled.div<{
  justifyContent?: 'space-around' | 'space-between' | 'space-evenly' | 'start' | 'end' | 'center';
  alignItems?: 'start' | 'end' | 'center';
  flexDirection?: 'row' | 'column';
  thin?: boolean;
  selected?: boolean;
}>`
  display: flex;
  width: 100%;
  min-height: 60px;
  margin-top: 10px;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  align-items: ${(props) => props.alignItems || 'center'};
  padding: ${(props) => (props.thin && '12px') || '20px'};
  ${(props) => props.selected && `background-color: ${colors.selectorBackground};`}
  border: 1px solid
  ${(props) => (props.selected ? colors.primaryButton : colors.selectorBorder)};
  border-radius: 2px;
  font-size: 13px;
`;

export const LargeSelectorLabel = styled.label<{
  alignItems?: 'start' | 'end' | 'center';
  flexDirection?: 'row' | 'column';
  fontSize?: number;
  lineHeight?: number;
}>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  align-items: ${(props) => props.alignItems || 'center'};
  font-weight: 600;
  font-size: ${(props) => props.fontSize || 18}px;
  line-height: ${(props) => props.lineHeight || 24}px;
`;

export const LargeSelectorSubForm = styled.div<{
  justifyContent?: 'space-around' | 'space-between' | 'space-evenly' | 'start' | 'end' | 'center';
  alignItems?: 'start' | 'end' | 'center';
  flexDirection?: 'row' | 'column';
}>`
  position: relative;
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  align-items: ${(props) => props.alignItems || 'center'};
  justify-content: ${(props) => props.justifyContent || 'space-between'};
`;

export const LargeSelectorChildren = styled.div<{
  justifyContent?: 'space-around' | 'space-between' | 'space-evenly' | 'start' | 'end' | 'center';
  alignItems?: 'start' | 'end' | 'center';
  flexDirection?: 'row' | 'column';
  paddingLeft?: number;
}>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'column'};
  align-items: ${(props) => props.alignItems || 'left'};
  ${(props) => props.justifyContent && `justify-content: ${props.justifyContent}`};
  width: 100%;
  ${(props) => props.paddingLeft && `padding-left: ${props.paddingLeft}px;`}

  & > *:nth-child(n + 2) {
    margin-top: 1em !important;
  }
`;

export const LargeSelectorRadio = styled.input`
  margin-right: 1em;
`;

export const LargeSelectorCheckbox = styled(Checkbox)<{ width?: string; height?: string }>`
  ${(props) => props.width && `width: ${props.width};`}
  ${(props) => props.height && `height: ${props.width};`}
  margin-right: 1em;
  margin-top: 0.25em;
  flex-shrink: 0;

  i {
    font-size: 0.75em;
  }
`;
